import { ExpressionType, HQLOperator } from '../types';

type AndOperatorDefinition = {
  expressionType: ExpressionType.VARIADIC;
  label: 'and';
  operator: HQLOperator.AND;
};

type ContainsOperatorDefinition = {
  expressionType: ExpressionType.BINARY;
  label: 'contains';
  operator: HQLOperator.CONTAINS;
};

type DoesNotContainOperatorDefinition = {
  expressionType: ExpressionType.BINARY;
  label: 'does not contain';
  operator: HQLOperator.DOES_NOT_CONTAIN;
};

type DoesNotEqualOperatorDefinition = {
  expressionType: ExpressionType.BINARY;
  label: 'does not equal';
  operator: HQLOperator.DOES_NOT_EQUAL;
};

type EqualsOperatorDefinition = {
  expressionType: ExpressionType.BINARY;
  label: 'is equal to';
  operator: HQLOperator.EQUALS;
};

type GreaterThanOperatorDefinition = {
  expressionType: ExpressionType.BINARY;
  label: 'is greater than';
  operator: HQLOperator.GREATER_THAN;
};

type IsAfterOperatorDefinition = {
  expressionType: ExpressionType.BINARY;
  label: 'is after';
  operator: HQLOperator.OCCURED_AFTER;
};

type IsAfterDayOperatorDefinition = {
  expressionType: ExpressionType.BINARY;
  label: 'is after day';
  operator: HQLOperator.OCCURRED_AFTER_RELATIVE;
};

type IsBeforeOperatorDefinition = {
  expressionType: ExpressionType.BINARY;
  label: 'is before';
  operator: HQLOperator.OCCURRED_BEFORE;
};

type IsBeforeDayOperatorDefinition = {
  expressionType: ExpressionType.BINARY;
  label: 'is before day';
  operator: HQLOperator.OCCURRED_BEFORE_RELATIVE;
};

type IsInYearOperatorDefinition = {
  expressionType: ExpressionType.BINARY;
  label: 'is in year';
  operator: HQLOperator.OCCURRED_IN_YEAR;
};

type IsOnOperatorDefinition = {
  expressionType: ExpressionType.BINARY;
  label: 'is on';
  operator: HQLOperator.OCCURRED_ON;
};

type IsOnDayOperatorDefinition = {
  expressionType: ExpressionType.BINARY;
  label: 'is on the day';
  operator: HQLOperator.OCCURRED_ON_DAY;
};

type NeverOccurredOperatorDefinition = {
  expressionType: ExpressionType.UNARY;
  label: 'never occurred';
  operator: HQLOperator.NEVER_OCCURRED;
};

type NullOperatorDefinition = {
  expressionType: ExpressionType.NULL;
  label: 'null';
  operator: HQLOperator.NULL;
};

type OccurredOperatorDefinition = {
  expressionType: ExpressionType.UNARY;
  label: 'occurred';
  operator: HQLOperator.OCCURRED;
};

type OrOperatorDefinition = {
  expressionType: ExpressionType.VARIADIC;
  label: 'or';
  operator: HQLOperator.OR;
};

type StartsWithOperatorDefinition = {
  expressionType: ExpressionType.BINARY;
  label: 'starts with';
  operator: HQLOperator.STARTS_WITH;
};

type OperatorDefinitions = {
  and: AndOperatorDefinition;
  contains: ContainsOperatorDefinition;
  doesNotContain: DoesNotContainOperatorDefinition;
  doesNotEqual: DoesNotEqualOperatorDefinition;
  equals: EqualsOperatorDefinition;
  greaterThan: GreaterThanOperatorDefinition;
  isAfter: IsAfterOperatorDefinition;
  isAfterDay: IsAfterDayOperatorDefinition;
  isBefore: IsBeforeOperatorDefinition;
  isBeforeDay: IsBeforeDayOperatorDefinition;
  isInYear: IsInYearOperatorDefinition;
  isOn: IsOnOperatorDefinition;
  isOnDay: IsOnDayOperatorDefinition;
  neverOccurred: NeverOccurredOperatorDefinition;
  null: NullOperatorDefinition;
  occurred: OccurredOperatorDefinition;
  or: OrOperatorDefinition;
  startsWith: StartsWithOperatorDefinition;
};

// Operators supported by the group builder parser
// Each operator has an associated 'expressionType' that it works with,
// a 'label' for customer display,
// and an 'operator' which is sent to the parser and used as an HQL operator
export const operatorDefinitions: OperatorDefinitions = Object.freeze({
  and: {
    expressionType: ExpressionType.VARIADIC,
    label: 'and',
    operator: HQLOperator.AND,
  },
  contains: {
    expressionType: ExpressionType.BINARY,
    label: 'contains',
    operator: HQLOperator.CONTAINS,
  },
  doesNotContain: {
    expressionType: ExpressionType.BINARY,
    label: 'does not contain',
    operator: HQLOperator.DOES_NOT_CONTAIN,
  },
  doesNotEqual: {
    expressionType: ExpressionType.BINARY,
    label: 'does not equal',
    operator: HQLOperator.DOES_NOT_EQUAL,
  },
  equals: {
    expressionType: ExpressionType.BINARY,
    label: 'is equal to',
    operator: HQLOperator.EQUALS,
  },
  greaterThan: {
    expressionType: ExpressionType.BINARY,
    label: 'is greater than',
    operator: HQLOperator.GREATER_THAN,
  },
  isAfter: {
    expressionType: ExpressionType.BINARY,
    label: 'is after',
    operator: HQLOperator.OCCURED_AFTER,
  },
  isAfterDay: {
    expressionType: ExpressionType.BINARY,
    label: 'is after day',
    operator: HQLOperator.OCCURRED_AFTER_RELATIVE,
  },
  isBefore: {
    expressionType: ExpressionType.BINARY,
    label: 'is before',
    operator: HQLOperator.OCCURRED_BEFORE,
  },
  isBeforeDay: {
    expressionType: ExpressionType.BINARY,
    label: 'is before day',
    operator: HQLOperator.OCCURRED_BEFORE_RELATIVE,
  },
  isInYear: {
    expressionType: ExpressionType.BINARY,
    label: 'is in year',
    operator: HQLOperator.OCCURRED_IN_YEAR,
  },
  isOn: {
    expressionType: ExpressionType.BINARY,
    label: 'is on',
    operator: HQLOperator.OCCURRED_ON,
  },
  isOnDay: {
    expressionType: ExpressionType.BINARY,
    label: 'is on the day',
    operator: HQLOperator.OCCURRED_ON_DAY,
  },
  neverOccurred: {
    expressionType: ExpressionType.UNARY,
    label: 'never occurred',
    operator: HQLOperator.NEVER_OCCURRED,
  },
  // FE internal operator, do not remove
  null: {
    expressionType: ExpressionType.NULL,
    label: 'null',
    operator: HQLOperator.NULL,
  },
  occurred: {
    expressionType: ExpressionType.UNARY,
    label: 'occurred',
    operator: HQLOperator.OCCURRED,
  },
  or: {
    expressionType: ExpressionType.VARIADIC,
    label: 'or',
    operator: HQLOperator.OR,
  },
  startsWith: {
    expressionType: ExpressionType.BINARY,
    label: 'starts with',
    operator: HQLOperator.STARTS_WITH,
  },
});
